import React, { Suspense, useState, useEffect, useRef } from 'react';
import { Fab, Tooltip, Box, Backdrop, CircularProgress, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddTimeEntryForm from '../Forms/AddTimeEntryForm.js';
import { lazyWithReload } from '../../utils/lazyWithReload';
import Timer from '../Timer/Timer.jsx';
import { useTeamspace } from '../../context/TeamspaceProvider';
import useIsPhone from '../../hooks/useIsPhone';
import { useFloatingButtonPosition, FAB_SIZE, PHONE_FAB_SIZE } from '../../hooks/useFloatingButtonPosition';

const DRAG_THRESHOLD = 5;

// Loaded the first time Import is opened (FAB-1, 2026-09-22). It imports the xlsx and
// exceljs spreadsheet libraries (~1.3 MB), and this button is on every page, so a
// static import put them in the main bundle every user downloads on first load.
const ImportTimeEntryModal = lazyWithReload(() => import('../Forms/ImportTimeEntryModal.jsx'));

const FloatingAddButton = () => {
  const { refreshTimeEntries } = useTeamspace();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Mount the (lazy) Import dialog on first open, then keep it mounted as before.
  const [importLoaded, setImportLoaded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [timerData, setTimerData] = useState(null);
  // Phones: smaller buttons nearer the edge, and visible labels on the menu
  // buttons (touch has no hover, so the tooltips never show). Desktop unchanged.
  const isPhone = useIsPhone();
  const menuFabSize = isPhone ? 48 : 56;
  const mainFabSize = isPhone ? PHONE_FAB_SIZE : FAB_SIZE;
  const menuLabel = (text) => isPhone && (
    <Paper elevation={3} sx={{ px: 1.25, py: 0.5, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
      {text}
    </Paper>
  );

  const handleOpen = () => {
    setTimerData(null); // Clear timer data for manual entry
    setOpen(true);
    setMenuOpen(false);
  };
  const handleClose = () => {
    setOpen(false);
    setTimerData(null); // Clear timer data when closing
  };

  const handleMenuToggle = () => {
    setMenuOpen(prev => !prev);
  };

  // --- Drag to move ---------------------------------------------------------
  // A press that moves less than DRAG_THRESHOLD px is a click (toggles the menu);
  // anything further is a drag, and the click that follows the drop is swallowed.
  const { position, setPosition, onLeftHalf, onTopHalf, viewport } = useFloatingButtonPosition();
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);

  const handlePointerDown = (e) => {
    if (e.button !== 0) return;
    dragRef.current = { x: e.clientX, y: e.clientY, start: position, moved: false };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = ev.clientX - d.x;
      const dy = ev.clientY - d.y;
      if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      if (!d.moved) {
        d.moved = true;
        setDragging(true);
        setMenuOpen(false);
      }
      d.last = { right: d.start.right - dx, bottom: d.start.bottom - dy };
      setPosition(d.last, { persist: false });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const d = dragRef.current;
      dragRef.current = null;
      if (d?.moved) {
        suppressClickRef.current = true;
        setDragging(false);
        setPosition(d.last); // persist the drop position
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const handleMainClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    handleMenuToggle();
  };

  const handleResetPosition = () => {
    setPosition(null);
    setMenuOpen(false);
  };

  const handleStartTimer = () => {
    // Trigger timer start via custom event
    const event = new CustomEvent('start-timer');
    window.dispatchEvent(event);
    setMenuOpen(false);
  };

  const handleOpenImport = () => {
    setImportLoaded(true);
    setImportOpen(true);
    setMenuOpen(false);
  };

  const handleCloseImport = () => {
    setImportOpen(false);
  };

  const handleImportComplete = (data) => {
    // Refresh calendar data
    if (refreshTimeEntries) {
      refreshTimeEntries();
    }
    // Could also dispatch events here if needed
  };

  // When a time entry is added successfully, close modal and broadcast refresh.
  // keepOpen is set by the form's "Save and add another" button: the entry is
  // saved and broadcast as usual, but the dialog stays open (already cleared)
  // so the next entry can be typed straight away.
  const handleTimeEntryAdded = (newTimeEntry, { keepOpen = false } = {}) => {
    // Refresh calendar data
    if (refreshTimeEntries) {
      refreshTimeEntries();
    }
    
    // Dispatch event with the new time entry data for optimistic updates
    const event = new CustomEvent('time-entry-added', { 
      detail: { timeEntry: newTimeEntry } 
    });
    window.dispatchEvent(event);
    if (!keepOpen) {
      handleClose();
    }
  };

  // Allow global trigger to open Add Time Entry from anywhere (optional)
  useEffect(() => {
    const listener = () => setOpen(true);
    window.addEventListener('open-add-time-entry', listener);
    return () => window.removeEventListener('open-add-time-entry', listener);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (menuOpen) {
      const handleClickOutside = (event) => {
        if (!event.target.closest('.floating-menu-container')) {
          setMenuOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  const menuFabSx = (delay) => ({
    bgcolor: theme.palette.secondary.main,
    color: theme.palette.text.primary,
    width: menuFabSize,
    height: menuFabSize,
    boxShadow: `0 8px 16px ${theme.palette.secondary.main}40`,
    '&:hover': {
      bgcolor: theme.palette.secondary.dark,
      boxShadow: `0 12px 24px ${theme.palette.secondary.main}80`,
      transform: 'scale(1.05)',
    },
    transition: 'all 0.3s ease',
    animation: `fabMenuIn 0.3s ease ${delay}s both`,
    '@keyframes fabMenuIn': {
      from: { opacity: 0, transform: `translateY(${onTopHalf ? -20 : 20}px)` },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
  });
  const menuItems = [
    { title: 'Start Timer', label: 'start timer', onClick: handleStartTimer, Icon: PlayArrowIcon },
    { title: 'Add Time Entry', label: 'add time entry', onClick: handleOpen, Icon: AddIcon },
    { title: 'Import Time Entries', label: 'import time entries', onClick: handleOpenImport, Icon: UploadFileIcon },
  ];
  const tooltipPlacement = onLeftHalf ? 'right' : 'left';

  return (
    <>
      <Timer 
        onTimeEntryAdded={handleTimeEntryAdded}
      />
      
      <Box
        className="floating-menu-container"
        sx={{
          position: 'fixed',
          // Phone, left half: the menu labels sit to the right of the buttons, so anchor
          // by the button's left edge to keep the buttons stacked over the main one.
          ...(isPhone && onLeftHalf
            ? { left: viewport.w - position.right - mainFabSize }
            : { right: position.right }),
          // In the top half the menu opens downward, so anchor by the button's top edge
          // to keep the main button still while the menu appears below it.
          ...(onTopHalf
            ? { top: viewport.h - position.bottom - mainFabSize }
            : { bottom: position.bottom }),
          zIndex: 1000,
          display: 'flex',
          flexDirection: onTopHalf ? 'column-reverse' : 'column',
          alignItems: isPhone && onLeftHalf ? 'flex-start' : 'flex-end',
          gap: 2,
        }}
      >
        {/* Menu Options - shown when menuOpen is true */}
        {menuOpen && menuItems.map(({ title, label, onClick, Icon }, i) => (
          <Box
            key={label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              // Phone label sits on the side away from the nearest screen edge.
              flexDirection: onLeftHalf ? 'row-reverse' : 'row',
            }}
          >
            {menuLabel(title)}
            <Tooltip title={title} placement={tooltipPlacement}>
              <Fab color="primary" aria-label={label} onClick={onClick} sx={menuFabSx(i * 0.1)}>
                <Icon sx={{ fontSize: 28 }} />
              </Fab>
            </Tooltip>
          </Box>
        ))}

        {/* Main Floating Button — click toggles the menu, drag moves it */}
        <Tooltip
          title={menuOpen ? 'Close Menu' : 'Add Time Entry (drag to move, double-click to reset)'}
          placement={tooltipPlacement}
          disableHoverListener={dragging}
        >
          <Fab
            color="primary"
            aria-label={menuOpen ? "close menu" : "add time entry"}
            onClick={handleMainClick}
            onDoubleClick={handleResetPosition}
            onPointerDown={handlePointerDown}
            sx={{
              bgcolor: theme.palette.secondary.main,
              color: theme.palette.text.primary,
              width: mainFabSize,
              height: mainFabSize,
              touchAction: 'none', // let pointer events drive the drag on touch screens
              cursor: dragging ? 'grabbing' : 'pointer',
              boxShadow: `0 8px 16px ${theme.palette.secondary.main}40`,
              '&:hover': {
                bgcolor: theme.palette.secondary.dark,
                boxShadow: `0 12px 24px ${theme.palette.secondary.main}80`,
                transform: dragging ? 'none' : 'scale(1.05)',
              },
              transition: dragging ? 'none' : 'all 0.3s ease',
              transform: menuOpen ? 'rotate(45deg)' : 'rotate(0deg)',
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 32 }} />
          </Fab>
        </Tooltip>
      </Box>

      <AddTimeEntryForm 
        open={open} 
        handleClose={handleClose} 
        onTimeEntryAdded={handleTimeEntryAdded}
        initialTimerData={timerData}
      />
      
      {importLoaded && (
        <Suspense fallback={<Backdrop open sx={{ zIndex: (t) => t.zIndex.modal + 1 }}><CircularProgress /></Backdrop>}>
          <ImportTimeEntryModal
            open={importOpen}
            handleClose={handleCloseImport}
            onImportComplete={handleImportComplete}
          />
        </Suspense>
      )}
    </>
  );
};

export default FloatingAddButton;

